import React from 'react';
import { AlertCircle, CheckCircle2, Info, Loader2, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button.jsx';

const alertStyles = {
  info: 'border-blue-200 bg-blue-50 text-blue-950 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100',
  warning: 'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100',
  error: 'border-rose-200 bg-rose-50 text-rose-950 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-100',
};

const alertIcons = { info: Info, success: CheckCircle2, warning: AlertCircle, error: AlertCircle };

export function Alert({ variant = 'info', title, children, className, ...props }) {
  const Icon = alertIcons[variant] || Info;
  return (
    <div role={variant === 'error' ? 'alert' : 'status'} className={cn('flex gap-3 rounded-xl border p-4 text-sm', alertStyles[variant], className)} {...props}>
      <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <div className="min-w-0">
        {title && <p className="font-bold">{title}</p>}
        <div className={title ? 'mt-1 leading-6' : 'leading-6'}>{children}</div>
      </div>
    </div>
  );
}

export function LoadingState({ message = 'Loading…', className }) {
  return (
    <div className={cn('flex min-h-[240px] flex-col items-center justify-center gap-3 text-center', className)} role="status" aria-live="polite">
      <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
      <p className="text-sm font-semibold text-muted-foreground">{message}</p>
    </div>
  );
}

export function ErrorState({ title = 'Something went wrong', message = 'Please try again.', onRetry, retryLabel = 'Try again', className }) {
  return (
    <div className={cn('flex min-h-[240px] flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-border bg-card p-8 text-center', className)} role="alert">
      <AlertCircle className="h-10 w-10 text-destructive" aria-hidden="true" />
      <div>
        <h2 className="font-display text-xl font-bold">{title}</h2>
        <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">{message}</p>
      </div>
      {onRetry && <Button variant="outline" onClick={onRetry}><RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />{retryLabel}</Button>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, className }) {
  return (
    <div className={cn('flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card/70 p-8 text-center', className)}>
      {Icon && <Icon className="mb-4 h-10 w-10 text-primary" aria-hidden="true" />}
      <h2 className="font-display text-xl font-bold">{title}</h2>
      {description && <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}
